import { z } from 'zod';
import type { NextAction, OpportunityStage, TaskType } from '../constants';
import type { TaskDTO } from './tasks';

/**
 * GET /api/dashboard?scope=me|team. `me` (default) = my leads and tasks — plus unassigned leads in the
 * "up for grabs" sections (high-priority uncontacted, recently discovered, duplicates). `team` = every lead
 * the user can see; managers and admins only.
 */
export const DASHBOARD_SCOPES = ['me', 'team'] as const;
export type DashboardScope = (typeof DASHBOARD_SCOPES)[number];
export const DashboardQuery = z.object({ scope: z.enum(DASHBOARD_SCOPES).default('me') });
export type DashboardQuery = z.infer<typeof DashboardQuery>;

/** A section list plus the total number of matching rows (for "+N more" links). */
export type DashboardList<T> = { items: T[]; total: number };

export type DashboardLeadDTO = {
  id: string;
  name: string;
  city: string | null;
  score: number;
  tierKey: string;
  nextAction: NextAction | null;
  nextActionReason: string | null;
  nextActionPriority: number | null;
  ownerId: string | null;
};

export type DashboardFollowUpDTO = {
  kind: 'task' | 'email';
  id: string;
  businessId: string | null;
  businessName: string | null;
  title: string;
  dueAt: string;
  /** Tasks only. */
  taskType: TaskType | null;
};

export type DashboardReplyDTO = {
  activityId: string;
  businessId: string;
  businessName: string;
  contactName: string | null;
  summary: string;
  occurredAt: string;
  emailId: string | null;
};

export type DashboardOpportunityDTO = {
  id: string;
  businessId: string;
  businessName: string;
  title: string;
  stage: OpportunityStage;
  estimatedValue: number;
  /** Manual probability or the stage default from settings. */
  probability: number;
  currency: string;
  expectedCloseDate: string | null;
};

export type DashboardQuoteDTO = {
  id: string;
  businessId: string;
  businessName: string;
  title: string;
  quoteSentAt: string;
  amount: number;
  currency: string;
  lastContactedAt: string | null;
};

export type DashboardDiscoveredDTO = {
  id: string;
  name: string;
  city: string | null;
  score: number;
  tierKey: string;
  sourceName: string | null;
  createdAt: string;
};

export type DashboardDuplicateDTO = {
  id: string;
  confidence: number;
  a: { id: string; name: string; city: string | null };
  b: { id: string; name: string; city: string | null };
};

export type LeadSourcePerformanceDTO = {
  /** null = leads without a recorded source. */
  sourceId: string | null;
  name: string;
  leads: number;
  contacted: number;
  responded: number;
  opportunities: number;
  won: number;
  wonValue: number;
};

export type DashboardMetricsDTO = {
  totalLeads: number;
  /** Created since Monday 00:00 in the user's timezone. */
  newLeadsThisWeek: number;
  /** Distinct leads with an outreach attempt in the last 30 days. */
  contactsAttempted: number;
  /** Leads contacted in the last 90 days, and how many of them responded in that window. */
  contacted90Days: number;
  responded90Days: number;
  /** responded / contacted × 100 over 90 days; null when nobody was contacted. */
  responseRate: number | null;
  activeOpportunities: number;
  pipelineValue: number;
  /** Σ value × (probability ?? stage default) / 100 over open opportunities. */
  weightedPipeline: number;
  /** Quotes sent in the last 30 days. */
  quotesSent: number;
  /** Won in the last 90 days. */
  dealsWon: { count: number; value: number };
  currency: string;
};

export type DashboardDTO = {
  scope: DashboardScope;
  generatedAt: string;
  timezone: string;
  attention: DashboardList<DashboardLeadDTO>;
  highPriorityUncontacted: DashboardList<DashboardLeadDTO>;
  overdueTasks: DashboardList<TaskDTO>;
  todayTasks: DashboardList<TaskDTO>;
  upcomingFollowUps: DashboardFollowUpDTO[];
  recentReplies: DashboardReplyDTO[];
  activeOpportunities: DashboardList<DashboardOpportunityDTO>;
  quotesAwaitingFollowUp: DashboardList<DashboardQuoteDTO>;
  recentlyDiscovered: { pendingReview: number; recent: DashboardDiscoveredDTO[] };
  pendingDuplicates: DashboardList<DashboardDuplicateDTO>;
  pendingApprovals: { count: number };
  metrics: DashboardMetricsDTO;
  leadSourcePerformance: LeadSourcePerformanceDTO[];
};
